import {Future} from '@raison/types';
import {atom, PrimitiveAtom} from 'jotai';
import {useMemo} from 'react';
import {Countdown} from '../ui/ResendTimer';

export function useResendTimerFromQuery(query: {
    data: {expiresAt: number; attemptsLeft?: number} | undefined;
    isFetching: boolean;
    isError: boolean;
}) {
    const {data: {expiresAt, attemptsLeft = Infinity} = {}, isFetching, isError} = query;

    type R = Future<Countdown>;

    const timer$ = useMemo((): PrimitiveAtom<R> => {
        if (isFetching) {
            return atom<R>({pending: true});
        }
        if (isError || !expiresAt) {
            return atom<R>({ok: false, error: true});
        }

        const timer$ = atom<R>({
            ok: true,
            value: {attempts: attemptsLeft, seconds: getSecondsUntilExpires(expiresAt), expiresAtMs: expiresAt},
        });

        timer$.onMount = set => {
            const intervalId = window.setInterval(() => {
                set(c => {
                    if (!c.ok) {
                        return c;
                    }
                    let remaining = Math.ceil(getSecondsUntilExpires(c.value.expiresAtMs));
                    if (remaining < 1) {
                        remaining = 0;
                        window.clearInterval(intervalId);
                    }
                    return {...c, value: {...c.value, seconds: remaining}};
                });
            }, 1000);

            return () => {
                window.clearInterval(intervalId);
            };
        };

        return timer$;
    }, [expiresAt, isFetching, isError]);

    return timer$;
}

function getSecondsUntilExpires(expiresAtMs: number): number {
    return (expiresAtMs - Date.now()) * 0.001;
}

export function getExpiryMsFromNow(after: number): number {
    return Date.now() + after * 1000;
}
